/**
 * Configurable fetch-with-retry for KobSign apps.
 *
 * Combines best features from all apps:
 * - 429 rate-limit handling with Retry-After (from developer-portal)
 * - 401 lockout prevention with timeout reset (from web)
 * - Exponential backoff on 5xx and network errors
 */

export interface FetchWithRetryOptions {
  /** Called on 401 Unauthorized. Each app handles this differently (clear data, redirect, etc.) */
  on401?: (response: Response) => void
  /**
   * Called when the backend responds with 403 + {"error": "captcha_required"}.
   * Should resolve with a fresh Turnstile token. The original request is then
   * retried once with ``X-Turnstile-Token`` set to the returned value.
   * Return empty string or throw to skip retry (will pass the 403 through).
   */
  onCaptchaRequired?: () => Promise<string>
}

/**
 * Creates a configured fetchWithRetry function.
 *
 * @example
 * ```ts
 * const fetchWithRetry = createFetchWithRetry({
 *   on401: () => { window.location.href = '/login' }
 * })
 * ```
 */
export function createFetchWithRetry(options?: FetchWithRetryOptions) {
  let isHandling401 = false
  let handling401Timeout: ReturnType<typeof setTimeout> | null = null
  let rateLimitedUntil = 0

  return async function fetchWithRetry(
    url: string,
    init: RequestInit,
    maxRetries: number = 3
  ): Promise<Response> {
    let lastError: Error | null = null
    let captchaRetryUsed = false

    // Wait if we're still rate-limited from a previous 429
    const rateLimitDelay = rateLimitedUntil - Date.now()
    if (rateLimitDelay > 0) {
      await new Promise((r) => setTimeout(r, rateLimitDelay))
    }

    for (let attempt = 0; attempt < maxRetries; attempt++) {
      try {
        const response = await fetch(url, init)

        // Handle 401 Unauthorized — delegate to app-specific handler
        if (response.status === 401 && !isHandling401) {
          isHandling401 = true
          // Reset flag after 5s to prevent permanent lockout
          if (handling401Timeout) clearTimeout(handling401Timeout)
          handling401Timeout = setTimeout(() => {
            isHandling401 = false
            handling401Timeout = null
          }, 5000)
          options?.on401?.(response)
          return response
        }

        // Handle 403 captcha_required: backend asks for a Turnstile token.
        // Try once per request to obtain one, retry with X-Turnstile-Token header.
        if (
          response.status === 403 &&
          !captchaRetryUsed &&
          options?.onCaptchaRequired
        ) {
          const cloned = response.clone()
          let isCaptchaRequired = false
          try {
            const body = await cloned.json()
            isCaptchaRequired = body?.error === 'captcha_required'
          } catch {
            // Body not JSON or already consumed — skip retry
          }
          if (isCaptchaRequired) {
            captchaRetryUsed = true
            try {
              const token = await options.onCaptchaRequired()
              if (token) {
                const headers = new Headers(init.headers || {})
                headers.set('X-Turnstile-Token', token)
                const retryInit = { ...init, headers }
                return await fetch(url, retryInit)
              }
            } catch {
              // Callback failed — fall through and return original 403
            }
            return response
          }
        }

        // On 429, record Retry-After so subsequent requests auto-delay.
        if (response.status === 429) {
          const retryAfter = response.headers.get('Retry-After')
          if (retryAfter) {
            const seconds = parseInt(retryAfter, 10)
            if (!isNaN(seconds)) {
              rateLimitedUntil = Date.now() + seconds * 1000
            }
          }
          return response
        }

        // Don't retry on 4xx (client errors)
        if (response.status >= 400 && response.status < 500) {
          return response
        }

        // Retry on 5xx (server errors)
        if (response.status >= 500) {
          lastError = new Error(`Server error: ${response.status}`)
          if (attempt < maxRetries - 1) {
            const delay = Math.pow(2, attempt) * 1000
            await new Promise((r) => setTimeout(r, delay))
            continue
          }
        }

        return response
      } catch (error) {
        // Network error (fetch throws exception)
        lastError = error as Error
        if (attempt < maxRetries - 1) {
          const delay = Math.pow(2, attempt) * 1000
          await new Promise((r) => setTimeout(r, delay))
        }
      }
    }

    throw lastError || new Error('Request failed after retries')
  }
}
